/**
 * @voltxampere/engine — B-source expression DSL lexer (Sprint 54.2b-4)
 *
 * Tokenises a B-source expression string into a flat Token stream
 * the recursive-descent parser consumes. ngspice-compatible syntax:
 *
 *   B1 OUT 0 V = IF (V(IN) > 0.5) ? PULSE(5, 0, 10m, 100u, 100u, 5m, 20m) : 0
 *
 * The B-element declaration prefix (`B1 OUT 0 V = `) is the SPICE
 * netlist responsibility; this lexer handles only the expression
 * RHS. `IF` keyword is optional — `(V(IN) > 0.5) ? a : b` is also
 * accepted (C-style ternary).
 *
 * Number suffixes: SPICE engineering notation (t/p/n/u/m/k/Meg/g).
 * `10m` parses as 0.010. Case-insensitive.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Reserved identifiers we recognise. `IF` is optional sugar that
 * the parser collapses into ternary form. Function names (`PULSE`,
 * `V`, `I`) are NOT reserved — they're plain IDENT and the parser
 * decides based on the next token (`LPAREN`).
 */
const KEYWORDS_LOWER = new Set(["if", "else"]);
/**
 * SPICE engineering-notation suffix table. Multipliers applied to
 * the bare number. Case-insensitive; `meg` and `m` are distinct
 * (1e6 vs 1e-3). Longest-prefix match in the lexer below.
 */
const SUFFIX_TABLE = [
    ["meg", 1e6],
    ["t", 1e12],
    ["g", 1e9],
    ["k", 1e3],
    ["mil", 25.4e-6], // 1 mil = 25.4 µm (rarely used but ngspice supports)
    ["m", 1e-3],
    ["u", 1e-6],
    ["µ", 1e-6],
    ["n", 1e-9],
    ["p", 1e-12],
    ["f", 1e-15],
];
function isDigit(c) {
    return c >= "0" && c <= "9";
}
function isIdentStart(c) {
    return (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "_";
}
function isIdentCont(c) {
    return isIdentStart(c) || isDigit(c);
}
/**
 * Tokenise the input source. Throws ContractViolationError on
 * unexpected character; never returns null. Always emits a final
 * EOF token so the parser can probe past the end cleanly.
 */
export function tokenize(source) {
    const tokens = [];
    let i = 0;
    const N = source.length;
    while (i < N) {
        const c = source[i];
        if (c === undefined)
            break;
        // Whitespace.
        if (c === " " || c === "\t" || c === "\n" || c === "\r") {
            i++;
            continue;
        }
        // Punctuation.
        if (c === "(") {
            tokens.push({ kind: "LPAREN", value: "(", position: i });
            i++;
            continue;
        }
        if (c === ")") {
            tokens.push({ kind: "RPAREN", value: ")", position: i });
            i++;
            continue;
        }
        if (c === ",") {
            tokens.push({ kind: "COMMA", value: ",", position: i });
            i++;
            continue;
        }
        if (c === "?") {
            tokens.push({ kind: "QMARK", value: "?", position: i });
            i++;
            continue;
        }
        if (c === ":") {
            tokens.push({ kind: "COLON", value: ":", position: i });
            i++;
            continue;
        }
        // Arithmetic operators.
        if (c === "+" || c === "-" || c === "*" || c === "/") {
            tokens.push({ kind: "OP", value: c, position: i });
            i++;
            continue;
        }
        // Comparisons (2-char first, then single char).
        if (c === "<" || c === ">" || c === "=" || c === "!") {
            const next = source[i + 1];
            if (next === "=") {
                const two = c + "=";
                tokens.push({ kind: "CMP", value: two, position: i });
                i += 2;
                continue;
            }
            if (c === "<" || c === ">") {
                tokens.push({ kind: "CMP", value: c, position: i });
                i++;
                continue;
            }
            // Bare `=` and `!` are illegal in B-source expressions
            // (assignment / negation forms not supported).
            throw new ContractViolationError(`dsl/lexer: unexpected '${c}' at position ${String(i)}`);
        }
        // Number (optionally with SPICE-suffix scaling).
        if (isDigit(c) || (c === "." && isDigit(source[i + 1] ?? ""))) {
            const start = i;
            let saw_digit = false;
            while (i < N && isDigit(source[i] ?? "")) {
                i++;
                saw_digit = true;
            }
            if (source[i] === ".") {
                i++;
                while (i < N && isDigit(source[i] ?? "")) {
                    i++;
                    saw_digit = true;
                }
            }
            // Scientific exponent.
            if (source[i] === "e" || source[i] === "E") {
                i++;
                if (source[i] === "+" || source[i] === "-")
                    i++;
                while (i < N && isDigit(source[i] ?? "")) {
                    i++;
                }
            }
            if (!saw_digit) {
                throw new ContractViolationError(`dsl/lexer: malformed number at position ${String(start)}`);
            }
            const numText = source.slice(start, i);
            let scale = 1;
            // Suffix probe (case-insensitive, longest match first).
            const rest = source.slice(i, i + 4).toLowerCase();
            for (const [suffix, factor] of SUFFIX_TABLE) {
                if (rest.startsWith(suffix)) {
                    // Reject suffix if continuing into an ident character.
                    const after = source[i + suffix.length] ?? "";
                    if (isIdentCont(after))
                        continue;
                    scale = factor;
                    i += suffix.length;
                    break;
                }
            }
            const num = parseFloat(numText) * scale;
            if (!Number.isFinite(num)) {
                throw new ContractViolationError(`dsl/lexer: non-finite number '${numText}' at position ${String(start)}`);
            }
            tokens.push({ kind: "NUMBER", value: String(num), position: start });
            continue;
        }
        // Identifier / keyword.
        if (isIdentStart(c)) {
            const start = i;
            while (i < N && isIdentCont(source[i] ?? "")) {
                i++;
            }
            const text = source.slice(start, i);
            const lower = text.toLowerCase();
            if (KEYWORDS_LOWER.has(lower)) {
                // Keywords are surfaced as IDENT with their lowercase form
                // — parser recognises by value.
                tokens.push({ kind: "IDENT", value: lower, position: start });
            }
            else {
                tokens.push({ kind: "IDENT", value: text, position: start });
            }
            continue;
        }
        throw new ContractViolationError(`dsl/lexer: unexpected character '${c}' at position ${String(i)}`);
    }
    tokens.push({ kind: "EOF", value: "", position: N });
    return tokens;
}
//# sourceMappingURL=lexer.js.map